import {useContext, useEffect, useState} from 'react';
import {CartDTO, CheckoutCartDTO} from '../../../api/models';
import {Grid, TextField, Typography} from '@mui/material';
import {TextComponent} from '../../RetailerWebScreen/components/TextComponent';
import {QRCodeSVG} from 'qrcode.react';
import {buildUpiURL} from '../../../api/payments';
import {checkoutCart} from '../../../api/cart.api';
import {useEventBatching} from '../../../api/events';
import {CartContext} from '../../../api/context';
import {useNavigate} from 'react-router-dom';

export const CartPaymentDetails = ({cartDTO}: {cartDTO: CartDTO}) => {
    const [transactionId, setTransactionId] = useState<string | undefined>();
    const [customerVPA, setCustomerVPA] = useState<string | undefined>();
    const [paymentUrl, setPaymentUrl] = useState<string | undefined>();

    useEffect(() => {
        const vpaToReceiveMoney = cartDTO.paymentDetails.receiverVPA;
        const amountToPay = cartDTO.paymentDetails.finalAmount / 100;
        const payeeName = cartDTO.paymentDetails.displayName;
        const cartId = cartDTO.cartId;
        const websiteId = cartDTO.websiteId;
        const note = `${websiteId} - ${cartId}`;
        const upiStringContent = buildUpiURL(vpaToReceiveMoney, amountToPay, payeeName, note);
        setPaymentUrl(upiStringContent);
    }, [cartDTO]);

    const buildCheckoutRequestFromCartDTO = (): CheckoutCartDTO | undefined => {
        if (transactionId && customerVPA) {
            const checkoutRequestDTO: CheckoutCartDTO = {
                items: cartDTO.items.map((item) => {
                    return {
                        catalogId: item.id,
                        quantity: item.quantity,
                    };
                }),
                websiteId: cartDTO.websiteId,
                transactionID: transactionId,
                customerVPA: customerVPA,
                paymentDetails: cartDTO.paymentDetails,
                cartId: cartDTO.cartId,
                buyerDTO: cartDTO.buyerDTO,
                createdOn: Date.now(),
            };
            return checkoutRequestDTO;
        }
    };

    return (
        <Grid style={{marginTop: 12}} container>
            <Grid item md={12} xs={12} sm={12}>
                <center>
                    <TextComponent variant='h5' content={'Payment Details'} fontWeight={400} />
                </center>
            </Grid>

            <br />
            <Grid item md={12} xs={12} sm={12}>
                {paymentUrl ? (
                    <>
                        <center>
                            <QRCodeSVG height={256} width={256} value={paymentUrl} />
                        </center>
                        <br />
                        <br />
                        <TextField
                            style={{
                                color: 'black',
                            }}
                            id={'transaction-id'}
                            label='Transaction ID'
                            variant='outlined'
                            fullWidth
                            value={transactionId}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setTransactionId(e.target.value);
                            }}
                        />
                        <br />
                        <br />
                        <TextField
                            style={{
                                color: 'black',
                            }}
                            id={'customer-vpa'}
                            label='Paid from UPI ID'
                            variant='outlined'
                            fullWidth
                            value={customerVPA}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setCustomerVPA(e.target.value);
                            }}
                        />
                        <br />
                        <br />
                        <CheckoutCartDetailsButton
                            isButtonDisabled={false}
                            getCheckoutRequestDTO={buildCheckoutRequestFromCartDTO}
                            transactionId={transactionId}
                            customerVpa={customerVPA}
                        />
                    </>
                ) : (
                    <></>
                )}
            </Grid>
        </Grid>
    );
};

export const CheckoutCartDetailsButton = ({
    transactionId,
    customerVpa,
    getCheckoutRequestDTO,
    isButtonDisabled,
}: {
    getCheckoutRequestDTO: () => CheckoutCartDTO | undefined;
    isButtonDisabled: boolean;
    transactionId?: string;
    customerVpa?: string;
}) => {
    const {trackEvent} = useEventBatching();
    const context = useContext(CartContext);
    const navigateTo = useNavigate();

    return (
        <Grid container>
            <Grid item md={3} xs={12} sm={12} lg={3} xl={3}>
                <div
                    onClick={async () => {
                        const checkoutRequestDTO = getCheckoutRequestDTO();
                        if (checkoutRequestDTO) {
                            try {
                                const response = await checkoutCart(checkoutRequestDTO);
                                context?.clearCart();
                                const orderId = response.id;
                                navigateTo(`/order/details?orderId=${orderId}`);
                            } catch (error) {
                                trackEvent({
                                    eventName: 'Checkout API Failed',
                                    props: {
                                        websiteId: checkoutRequestDTO.websiteId,
                                        cartId: checkoutRequestDTO.cartId,
                                        createdOn: checkoutRequestDTO.createdOn.toString(),
                                        transactionId: checkoutRequestDTO.transactionID,
                                        customerVpa: checkoutRequestDTO.customerVPA,
                                        buyerDTO: JSON.stringify(checkoutRequestDTO.buyerDTO),
                                        paymentDetails: JSON.stringify(checkoutRequestDTO.paymentDetails),
                                        items: JSON.stringify(checkoutRequestDTO.items),
                                    },
                                });
                            }
                        } else {
                            // track cases where checkout request was not built
                            trackEvent({
                                eventName: 'Checkout Request Build Failed',
                                props: {
                                    transactionId: transactionId ?? '',
                                    customerVpa: customerVpa ?? '',
                                },
                            });
                        }
                    }}
                    style={{
                        paddingTop: 12,
                        paddingBottom: 12,
                        paddingLeft: 32,
                        borderRadius: 8,
                        paddingRight: 32,
                        cursor: isButtonDisabled ? 'default' : 'pointer',
                        backgroundColor: isButtonDisabled ? 'gray' : '#15171c',
                    }}>
                    <center>
                        <Typography
                            sx={{
                                fontWeight: 700,
                                color: 'white',
                                fontFamily: 'Poppins',
                            }}
                            variant='body1'>
                            Place order
                        </Typography>
                    </center>
                </div>
            </Grid>
        </Grid>
    );
};
